import { pollInterval } from '../realtime/live-updates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AppNotification,
  CollectionMeta,
  NotificationCategory,
  NotificationListQuery,
  NotificationPreferences,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['notifications'] as const;
/** Cheap enough to poll continuously while the app is open - drives the topbar badge. */
const UNREAD_POLL_MS = 20_000;

export interface NotificationPage {
  items: AppNotification[];
  meta: CollectionMeta;
}

function toQuery(q: NotificationListQuery): Record<string, string | number | undefined> {
  return { page: q.page, pageSize: q.pageSize, unreadOnly: q.unreadOnly ? 'true' : undefined };
}

export async function listNotifications(q: NotificationListQuery = {}): Promise<NotificationPage> {
  const res = await apiClient.getCollection<AppNotification>('/notifications', {
    query: toQuery(q),
  });
  return { items: res.data, meta: res.meta };
}
export const getUnreadCount = () => apiClient.get<{ count: number }>('/notifications/unread-count');
export const markNotificationRead = (id: string) =>
  apiClient.post<void>(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => apiClient.post<void>('/notifications/read-all');

export function useNotifications(query: NotificationListQuery = {}, enabled = true) {
  return useQuery<NotificationPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listNotifications(query),
    enabled,
  });
}

/** Polls continuously (only while a component actually uses it) for the topbar badge. */
export function useUnreadCount() {
  return useQuery<{ count: number }, ApiClientError>({
    queryKey: [...KEY, 'unread-count'],
    queryFn: getUnreadCount,
    refetchInterval: pollInterval(UNREAD_POLL_MS),
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: markNotificationRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

type Preferences = Record<NotificationCategory, boolean>;

export const getNotificationPreferences = () =>
  apiClient.get<Preferences>('/notifications/preferences');
export const updateNotificationPreferences = (prefs: NotificationPreferences) =>
  apiClient.put<Preferences>('/notifications/preferences', prefs);

export function useNotificationPreferences() {
  return useQuery<Preferences, ApiClientError>({
    queryKey: [...KEY, 'preferences'],
    queryFn: getNotificationPreferences,
  });
}

export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation<Preferences, ApiClientError, NotificationPreferences>({
    mutationFn: updateNotificationPreferences,
    onSuccess: (data) => qc.setQueryData([...KEY, 'preferences'], data),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, void>({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
